import { Component, inject, OnInit } from '@angular/core';
import { AbstractControlOptions, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ControlConfig, FormGroupConfig } from '../../form-config.model';
import { Anlagenkennlinie } from '../../../../domain/model/anlagenkennlinie.model';
import { ErzeugungsanlageConfig } from '../../erzeugungsanlage.config';
import { AnlagenkennlinienValidator } from '../../validator/anlagenkennlinien.validator';
import { Feld } from '../../../../domain/model/feld.enum';
import { FeldBeschreibungService } from '../../../../service/feldbeschreibung/feldbeschreibung.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface AnlagenkennlinieSubFormData {
    form: UntypedFormGroup,
    disabled: boolean,
    readonly: boolean,
    isAnlagenkennlinieRequired: boolean,
}

@Component({
    selector: 'redispatch-anlagenkennlinie-sub-form',
    templateUrl: './anlagenkennlinie-sub-form.component.html',
    styleUrls: [
        './anlagenkennlinie-sub-form.component.scss',
    ],
    standalone: false,
})
export class AnlagenkennlinieSubFormComponent implements OnInit {

    public readonly LYT_GAP = ErzeugungsanlageConfig.HORIZONTAL_FX_LAYOUT_GAP;
    public readonly ERROR_MIN_VALUES = ErzeugungsanlageConfig.ANLAGENKENNLINIE_MIN_ERROR;
    public readonly Feld = Feld;

    public form: UntypedFormGroup = null;
    public disabled = false;
    public readonly = false;
    public data: AnlagenkennlinieSubFormData = inject(MAT_DIALOG_DATA);

    private initialAnlagenkennlinienData: Anlagenkennlinie;
    private feldBeschreibungService = inject(FeldBeschreibungService);
    private dialogRef = inject(MatDialogRef<AnlagenkennlinieSubFormComponent>);

    public get f(): ControlConfig<Anlagenkennlinie> {
        return this.form.controls as ControlConfig<Anlagenkennlinie>;
    }

    public get hilfetext(): Observable<string | null> {
        return this.feldBeschreibungService.getCachedFeldBeschreibung(Feld.HEADER_TR_WIND_ANLAGENKENNLINIE).pipe(
            map(value => value?.hilfetext),
        );
    }

    static formGroupConfig(initialDisabled: boolean): FormGroupConfig<Anlagenkennlinie> {
        const validator = [
            Validators.min(0),
            Validators.pattern(new RegExp('^\\d*$')),
        ];

        return {
            kwBei01ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei02ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei03ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei04ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei05ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei06ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei07ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei08ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei09ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei10ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei11ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei12ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei13ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei14ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei15ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei16ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei17ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei18ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei19ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei20ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei21ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei22ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei23ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei24ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei25ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei26ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei27ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei28ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei29ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei30ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei31ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei32ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei33ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei34ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
            kwBei35ms: [
                { value: null, disabled: initialDisabled },
                validator,
            ],
        };
    }

    static formOptions(isAnlagenkennlinieRequired: () => boolean): AbstractControlOptions | null {
        return {
            validators: AnlagenkennlinienValidator.validate(isAnlagenkennlinieRequired),
        };
    }

    ngOnInit(): void {
        this.form = this.data.form;
        this.disabled = this.data.disabled;
        this.readonly = this.data.readonly;
        this.initialAnlagenkennlinienData = this.data.form.value;
        if (this.disabled || this.readonly) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    public close(rollback: boolean = false) {
        if (rollback) {
            this.form.reset(this.initialAnlagenkennlinienData);
        }
        if (!this.readonly && !this.disabled) {
            this.form.markAllAsTouched();
        }
        this.dialogRef.close();
    }
}
